import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { getInviteDetails, acceptOrganizationInvite } from '../utils/organizationsApi';
import { getTeamInviteDetails, acceptTeamInvite } from '../utils/teamsApi';

const InvitePage = () => {
  const { type, token } = useParams();
  const { isAuthenticated, refreshUser, loading: authLoading } = useAuth();
  const { refreshOrganizations } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isOrgInvite = type === 'org';
  const isTeamInvite = type === 'team';

  useEffect(() => {
    const fetchInviteDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        if (isOrgInvite) {
          data = await getInviteDetails(token);
        } else if (isTeamInvite) {
          data = await getTeamInviteDetails(token);
        } else {
          throw new Error('Invalid invite type');
        }
        setInviteData(data);
      } catch (err) {
        console.error('Error fetching invite:', err);
        setError(err.response?.data?.message || 'Invalid or expired invite link');
      } finally {
        setLoading(false);
      }
    };

    fetchInviteDetails();
  }, [token, isOrgInvite, isTeamInvite]);

  const handleAcceptInvite = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }

    setAccepting(true);
    setError(null);
    try {
      if (isOrgInvite) {
        await acceptOrganizationInvite(token);
      } else {
        await acceptTeamInvite(token);
      }
      setSuccess(true);
      // Accepting an org invite marks the user onboarded server-side; refresh the
      // auth user so the client clears the onboarding gate before they navigate
      // into the workspace (otherwise the stale null flag would bounce them to
      // /onboarding).
      await refreshUser();
      await refreshOrganizations();
    } catch (err) {
      console.error('Error accepting invite:', err);
      setError(err.response?.data?.message || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const handleGoToWorkspace = () => {
    if (isOrgInvite && inviteData?.organization) {
      navigate(`/organizations/${inviteData.organization.id}`);
    } else if (isTeamInvite && inviteData?.team) {
      navigate(`/teams/${inviteData.team.id}`);
    } else {
      navigate('/organizations');
    }
  };

  if (authLoading || loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Successfully Joined!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have joined{' '}
            {isOrgInvite
              ? inviteData?.organization?.name
              : isTeamInvite
              ? `${inviteData?.team?.name} in ${inviteData?.organization?.name}`
              : 'the workspace'}
          </Typography>
          <Button variant="contained" onClick={handleGoToWorkspace}>
            Go to {isOrgInvite ? 'Organization' : 'Team'}
          </Button>
        </Paper>
      </Container>
    );
  }

  const orgInfo = isOrgInvite ? inviteData?.organization : inviteData?.organization;
  const teamInfo = isTeamInvite ? inviteData?.team : null;

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
        {/* Organization/Team Logo */}
        {orgInfo?.logo ? (
          <Avatar
            src={orgInfo.logo}
            sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
          />
        ) : (
          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
            {isTeamInvite ? <GroupIcon sx={{ fontSize: 40 }} /> : <BusinessIcon sx={{ fontSize: 40 }} />}
          </Avatar>
        )}

        {/* Invite Title */}
        <Typography variant="h5" gutterBottom>
          {isTeamInvite
            ? `Join ${teamInfo?.name}`
            : `Join ${orgInfo?.name}`}
        </Typography>

        {/* Additional Info */}
        {isTeamInvite && orgInfo && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            in {orgInfo.name}
          </Typography>
        )}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You've been invited to join this{' '}
          {isTeamInvite ? 'team' : 'organization'}
        </Typography>

        {/* Expiration Warning */}
        {inviteData?.expiresAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            This invite expires on{' '}
            {new Date(inviteData.expiresAt).toLocaleDateString()}
          </Typography>
        )}

        {/* Action Button */}
        {!isAuthenticated ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please sign in to accept this invite
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleAcceptInvite}
            >
              Sign In to Join
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={handleAcceptInvite}
            disabled={accepting}
          >
            {accepting ? <CircularProgress size={24} /> : 'Accept Invite'}
          </Button>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default InvitePage;
